/**
 * Analytics tracking for Glide Deposit Widget
 * This module handles sending analytics events from the iframe to the parent window
 */

import mixpanel from "mixpanel-browser";
import { logger } from "./logger";

/**
 * Mixpanel configuration
 */
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN || "";
const TRACK_EVENTS = import.meta.env.VITE_TRACK_EVENTS === "true";

if (import.meta.env.DEV) {
  console.log(
    "[Analytics] MIXPANEL_TOKEN:",
    MIXPANEL_TOKEN ? "configured" : "missing",
  );
  console.log("[Analytics] TRACK_EVENTS:", TRACK_EVENTS);
}

/**
 * Device context for analytics
 */
export type DeviceContext = {
  isMobile: boolean;
  platform: "ios" | "android" | "windows" | "macos" | "linux" | "unknown";
  browser: string;
};

/**
 * Analytics context included with every event
 */
export type AnalyticsContext = {
  // Project identification
  projectId?: string;
  appSlug: string;

  // SDK and environment info
  sdkVersion: string;
  referrerDomain?: string;

  // User identification
  walletAddress?: string;

  // Device context
  device: DeviceContext;
};

/**
 * Raw event types (before context is added)
 */
export type GlideAnalyticsEventType =
  // Modal lifecycle events
  | { type: "modal_opened"; timestamp: number }
  | { type: "modal_closed"; timestamp: number; completedTransaction: boolean }

  // Mode selection
  | {
      type: "mode_selected";
      mode: "deposit" | "withdraw" | "pay" | "call" | "buy";
      timestamp: number;
    }

  // Step navigation events
  | { type: "step_viewed"; step: string; timestamp: number }
  | {
      type: "step_exited";
      step: string;
      timestamp: number;
      direction: "forward" | "back";
    }

  // Payment method selection
  | {
      type: "payment_method_selected";
      paymentMethod:
        | "wallet"
        | "external_wallet"
        | "transfer"
        | "coinbase"
        | "coinbase_app"
        | "fiat"
        | "interac"
        | "debit_card_alias"
        | "onramp"
        | "onramper";
      timestamp: number;
    }

  // Funding source events
  | {
      type: "funding_source_viewed";
      availableOptions: string[];
      timestamp: number;
    }
  | {
      type: "funding_source_selected";
      fundingSource: "wallet" | "crypto" | "fiat";
      timestamp: number;
    }

  // Wallet connection
  | { type: "wallet_connect_started"; timestamp: number }
  | {
      type: "wallet_connected";
      address: string;
      chainId: number;
      timestamp: number;
    }
  | { type: "wallet_connection_failed"; error: string; timestamp: number }
  | { type: "wallet_disconnected"; timestamp: number }

  // Chain/Currency selection
  | {
      type: "chain_selected";
      chainId: string;
      chainName: string;
      timestamp: number;
    }
  | {
      type: "currency_selected";
      currencyId: string;
      currencySymbol: string;
      chainId: string;
      timestamp: number;
    }

  // Amount entry
  | {
      type: "amount_entered";
      amount: string;
      currencySymbol: string;
      amountUSD?: number;
      timestamp: number;
    }

  // Session creation
  | { type: "session_creation_started"; timestamp: number }
  | {
      type: "session_created";
      sessionId: string;
      paymentMethod: string;
      estimatedAmountUSD?: number;
      timestamp: number;
    }
  | { type: "session_creation_failed"; error: string; timestamp: number }

  // Transaction events (wallet payments)
  | {
      type: "transaction_started";
      sessionId: string;
      paymentMethod: string;
      fromToken: string;
      toToken: string;
      amount: string;
      estimatedAmountUSD?: number;
      timestamp: number;
    }
  | {
      type: "transaction_signature_requested";
      sessionId: string;
      timestamp: number;
    }
  | {
      type: "transaction_signed";
      sessionId: string;
      txHash: string;
      timestamp: number;
    }
  | {
      type: "transaction_signature_rejected";
      sessionId: string;
      timestamp: number;
    }
  | {
      type: "transaction_completed";
      sessionId: string;
      txHash: string;
      paymentMethod: string;
      success: boolean;
      amountUSD?: number;
      timestamp: number;
    }
  | {
      type: "transaction_failed";
      sessionId: string;
      error: string;
      paymentMethod: string;
      timestamp: number;
    }

  // Onramp specific events
  | {
      type: "onramp_window_opened";
      provider: "coinbase" | "moonpay";
      timestamp: number;
    }
  | {
      type: "onramp_window_closed";
      provider: "coinbase" | "moonpay";
      completed: boolean;
      timestamp: number;
    }

  // Transfer deposit events
  | {
      type: "deposit_address_generated";
      sessionId: string;
      chainId: string;
      currencySymbol: string;
      timestamp: number;
    }
  | {
      type: "deposit_address_copied";
      sessionId: string;
      timestamp: number;
    }
  | {
      type: "waiting_for_deposit";
      sessionId: string;
      chainId: string;
      currencySymbol: string;
      timestamp: number;
    }
  | {
      type: "deposit_detected";
      sessionId: string;
      amount: string;
      currencySymbol: string;
      timestamp: number;
    }
  | {
      type: "transfer_completed";
      sessionId: string;
      txHash: string;
      chainId: string;
      currencySymbol: string;
      amount: string;
      amountUSD?: number;
      timestamp: number;
    }

  // Error events
  | {
      type: "error_occurred";
      errorType: string;
      errorMessage: string;
      step?: string;
      timestamp: number;
    }

  // Conversion events
  | {
      type: "conversion_completed";
      sessionId: string;
      paymentMethod: string;
      amountUSD?: number;
      duration: number; // ms from modal_opened to completion
      timestamp: number;
    };

/**
 * Full event with context (what gets sent to parent)
 */
export type GlideAnalyticsEvent = GlideAnalyticsEventType & AnalyticsContext;

/**
 * Detect device platform
 */
function detectPlatform(): DeviceContext["platform"] {
  if (typeof navigator === "undefined") return "unknown";

  const ua = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || "";

  // Check for iOS (iPhone, iPad, iPod)
  if (
    /iphone|ipad|ipod/.test(ua) ||
    (platform === "macintel" && navigator.maxTouchPoints > 1)
  ) {
    return "ios";
  }

  // Check for Android
  if (/android/.test(ua)) {
    return "android";
  }

  // Check for Windows
  if (/win/.test(platform) || /windows/.test(ua)) {
    return "windows";
  }

  // Check for macOS
  if (/mac/.test(platform) && !/iphone|ipad|ipod/.test(ua)) {
    return "macos";
  }

  // Check for Linux
  if (/linux/.test(platform) || /linux/.test(ua)) {
    return "linux";
  }

  return "unknown";
}

/**
 * Detect browser name
 */
function detectBrowser(): string {
  if (typeof navigator === "undefined") return "unknown";

  const ua = navigator.userAgent;

  // Check for common browsers (order matters - check more specific first)
  if (ua.includes("Firefox")) return "firefox";
  if (ua.includes("Edg")) return "edge";
  if (ua.includes("Chrome")) return "chrome";
  if (ua.includes("Safari")) return "safari";
  if (ua.includes("Opera") || ua.includes("OPR")) return "opera";

  return "unknown";
}

/**
 * Detect if mobile device
 */
function detectIsMobile(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/.test(
    ua,
  );
}

/**
 * Get device context
 */
function getDeviceContext(): DeviceContext {
  return {
    isMobile: detectIsMobile(),
    platform: detectPlatform(),
    browser: detectBrowser(),
  };
}

class WidgetAnalytics {
  // Internal tracking (for us - always enabled once initialized)
  private internalTrackingEnabled = false;
  // Client callback tracking (only if SDK provides callback)
  private clientCallbackEnabled = false;
  // Mixpanel initialized flag
  private mixpanelInitialized = false;

  private startTime?: number;
  private currentStep?: string;

  // Context fields
  private projectId?: string;
  private appSlug = "";
  private sdkVersion = "";
  private referrerDomain?: string;
  private walletAddress?: string;
  private device: DeviceContext = getDeviceContext();

  /**
   * Check if Mixpanel should be enabled
   */
  private shouldEnableMixpanel(): boolean {
    if (!MIXPANEL_TOKEN) {
      logger.info("Mixpanel token not configured, skipping initialization");
      return false;
    }

    if (!TRACK_EVENTS) {
      logger.info("VITE_TRACK_EVENTS not enabled, skipping Mixpanel");
      return false;
    }

    return true;
  }

  /**
   * Initialize Mixpanel
   */
  private initializeMixpanel() {
    if (this.mixpanelInitialized) return;
    if (!this.shouldEnableMixpanel()) return;

    try {
      mixpanel.init(MIXPANEL_TOKEN, {
        autocapture: true,
        record_sessions_percent: 100,
        persistence: "localStorage",
        ip: true, // Track IP for geolocation
      });

      this.mixpanelInitialized = true;
      if (import.meta.env.DEV) {
        console.log("[Analytics] Mixpanel initialized successfully");
      }
      logger.info("Mixpanel initialized successfully");
    } catch (error) {
      logger.error(`Failed to initialize Mixpanel: ${error}`);
    }
  }

  /**
   * Initialize internal tracking (for our Mixpanel).
   * Called early with data from URL/appConfig - works without SDK.
   */
  initializeInternal(config: { projectId: string; appSlug: string }) {
    this.projectId = config.projectId;
    this.appSlug = config.appSlug;
    this.device = getDeviceContext();
    this.internalTrackingEnabled = true;

    // Initialize Mixpanel
    this.initializeMixpanel();

    // Register super properties (included in all events)
    if (this.mixpanelInitialized) {
      mixpanel.register({
        projectId: this.projectId,
        appSlug: this.appSlug,
        device_isMobile: this.device.isMobile,
        device_platform: this.device.platform,
        device_browser: this.device.browser,
      });
    }

    logger.info(
      `Internal analytics initialized: projectId=${this.projectId}, appSlug=${this.appSlug}`,
    );
  }

  /**
   * Update with SDK-provided data and enable client callback if provided.
   * Called when SDK sends initialize message.
   */
  initializeFromSDK(config: {
    hasAnalyticsCallback: boolean;
    projectId?: string;
    appSlug?: string;
    sdkVersion?: string;
    referrerDomain?: string;
    connectedWalletAddress?: string;
  }) {
    this.clientCallbackEnabled = config.hasAnalyticsCallback;

    // SDK can provide additional/override data
    if (config.projectId) this.projectId = config.projectId;
    if (config.appSlug) this.appSlug = config.appSlug;
    if (config.sdkVersion) this.sdkVersion = config.sdkVersion;
    if (config.referrerDomain) this.referrerDomain = config.referrerDomain;
    if (config.connectedWalletAddress)
      this.walletAddress = config.connectedWalletAddress;

    // Ensure internal tracking is enabled
    this.internalTrackingEnabled = true;

    // Initialize Mixpanel if not already done
    this.initializeMixpanel();

    // Update Mixpanel super properties with SDK data
    if (this.mixpanelInitialized) {
      mixpanel.register({
        projectId: this.projectId,
        appSlug: this.appSlug,
        sdkVersion: this.sdkVersion,
        referrerDomain: this.referrerDomain,
      });

      // Identify user if wallet is connected
      if (config.connectedWalletAddress) {
        this.setWalletAddress(config.connectedWalletAddress);
      }
    }

    if (this.clientCallbackEnabled) {
      logger.info(
        `Client analytics callback enabled: projectId=${this.projectId}, appSlug=${this.appSlug}, sdkVersion=${this.sdkVersion}`,
      );
    }
  }

  /**
   * @deprecated Use initializeFromSDK instead
   */
  initialize(config: {
    hasAnalyticsCallback: boolean;
    projectId?: string;
    appSlug?: string;
    sdkVersion?: string;
    referrerDomain?: string;
    connectedWalletAddress?: string;
  }) {
    this.initializeFromSDK(config);
  }

  /**
   * Update wallet address (called when user connects/disconnects wallet)
   */
  setWalletAddress(address: string | undefined) {
    this.walletAddress = address;

    // Identify user in Mixpanel by wallet address
    if (this.mixpanelInitialized) {
      if (address) {
        mixpanel.identify(address);
        mixpanel.people.set({
          $name: address,
          walletAddress: address,
          lastSeen: new Date().toISOString(),
        });
      } else {
        // User disconnected - just clear the wallet property, don't reset
        // This preserves the anonymous device ID for tracking
        mixpanel.people.set({
          walletAddress: null,
        });
      }
    }
  }

  /**
   * Get current analytics context
   */
  private getContext(): AnalyticsContext {
    return {
      projectId: this.projectId,
      appSlug: this.appSlug,
      sdkVersion: this.sdkVersion,
      referrerDomain: this.referrerDomain,
      walletAddress: this.walletAddress,
      device: this.device,
    };
  }

  track(event: GlideAnalyticsEventType) {
    // Merge event with context
    const fullEvent: GlideAnalyticsEvent = {
      ...event,
      ...this.getContext(),
    };

    // 1. Internal tracking (for our Mixpanel) - always if initialized
    if (this.internalTrackingEnabled) {
      try {
        this.trackInternal(fullEvent);
      } catch (error) {
        logger.error(`Error in internal analytics: ${error}`);
      }
    }

    // 2. Client callback tracking - only if SDK enabled it
    if (this.clientCallbackEnabled) {
      try {
        window.parent?.postMessage(
          {
            type: "analyticsEvent",
            event: fullEvent,
          },
          "*",
        );
        logger.info(`Analytics event sent to client: ${event.type}`);
      } catch (error) {
        logger.error(`Error sending analytics to client: ${error}`);
      }
    }
  }

  /**
   * Internal tracking implementation (for our Mixpanel).
   */
  private trackInternal(event: GlideAnalyticsEvent) {
    if (!this.mixpanelInitialized) {
      return;
    }

    try {
      // Track the event in Mixpanel
      // Event properties are flattened for easier querying in Mixpanel
      const eventData = {
        // Event-specific properties (spread all event fields)
        ...event,
        // Flatten device object for easier filtering in Mixpanel
        device_isMobile: event.device?.isMobile,
        device_platform: event.device?.platform,
        device_browser: event.device?.browser,
      };

      if (import.meta.env.DEV) {
        console.log("[Analytics] Tracking event:", event.type, eventData);
      }
      mixpanel.track(event.type, eventData);

      logger.info(`Mixpanel event tracked: ${event.type}`);
    } catch (error) {
      logger.error(`Failed to track Mixpanel event: ${error}`);
    }
  }

  // Convenience methods for common events
  modalOpened() {
    this.startTime = Date.now();
    this.track({ type: "modal_opened", timestamp: Date.now() });
  }

  modalClosed(completedTransaction: boolean) {
    this.track({
      type: "modal_closed",
      timestamp: Date.now(),
      completedTransaction,
    });
    this.startTime = undefined;
    this.currentStep = undefined;
  }

  modeSelected(mode: "deposit" | "withdraw" | "pay" | "call" | "buy") {
    this.track({ type: "mode_selected", mode, timestamp: Date.now() });
  }

  stepViewed(step: string) {
    this.currentStep = step;
    this.track({ type: "step_viewed", step, timestamp: Date.now() });
  }

  stepExited(step: string, direction: "forward" | "back") {
    this.track({ type: "step_exited", step, timestamp: Date.now(), direction });
  }

  paymentMethodSelected(
    paymentMethod:
      | "wallet"
      | "external_wallet"
      | "transfer"
      | "coinbase"
      | "coinbase_app"
      | "fiat"
      | "interac"
      | "debit_card_alias"
      | "onramp"
      | "onramper",
  ) {
    this.track({
      type: "payment_method_selected",
      paymentMethod,
      timestamp: Date.now(),
    });
  }

  fundingSourceViewed(availableOptions: string[]) {
    this.track({
      type: "funding_source_viewed",
      availableOptions,
      timestamp: Date.now(),
    });
  }

  fundingSourceSelected(fundingSource: "wallet" | "crypto" | "fiat") {
    this.track({
      type: "funding_source_selected",
      fundingSource,
      timestamp: Date.now(),
    });
  }

  walletConnectStarted() {
    this.track({ type: "wallet_connect_started", timestamp: Date.now() });
  }

  walletConnected(address: string, chainId: number) {
    // Update stored wallet address
    this.setWalletAddress(address);
    this.track({
      type: "wallet_connected",
      address,
      chainId,
      timestamp: Date.now(),
    });
  }

  walletConnectionFailed(error: string) {
    this.track({
      type: "wallet_connection_failed",
      error,
      timestamp: Date.now(),
    });
  }

  walletDisconnected() {
    this.setWalletAddress(undefined);
    this.track({ type: "wallet_disconnected", timestamp: Date.now() });
  }

  chainSelected(chainId: string, chainName: string) {
    this.track({
      type: "chain_selected",
      chainId,
      chainName,
      timestamp: Date.now(),
    });
  }

  currencySelected(
    currencyId: string,
    currencySymbol: string,
    chainId: string,
  ) {
    this.track({
      type: "currency_selected",
      currencyId,
      currencySymbol,
      chainId,
      timestamp: Date.now(),
    });
  }

  amountEntered(amount: string, currencySymbol: string, amountUSD?: number) {
    this.track({
      type: "amount_entered",
      amount,
      currencySymbol,
      amountUSD,
      timestamp: Date.now(),
    });
  }

  sessionCreationStarted() {
    this.track({ type: "session_creation_started", timestamp: Date.now() });
  }

  sessionCreated(
    sessionId: string,
    paymentMethod: string,
    estimatedAmountUSD?: number,
  ) {
    this.track({
      type: "session_created",
      sessionId,
      paymentMethod,
      estimatedAmountUSD,
      timestamp: Date.now(),
    });
  }

  sessionCreationFailed(error: string) {
    this.track({
      type: "session_creation_failed",
      error,
      timestamp: Date.now(),
    });
  }

  transactionStarted(
    sessionId: string,
    paymentMethod: string,
    fromToken: string,
    toToken: string,
    amount: string,
    estimatedAmountUSD?: number,
  ) {
    this.startTime = Date.now();
    this.track({
      type: "transaction_started",
      sessionId,
      paymentMethod,
      fromToken,
      toToken,
      amount,
      estimatedAmountUSD,
      timestamp: Date.now(),
    });
  }

  transactionSignatureRequested(sessionId: string) {
    this.track({
      type: "transaction_signature_requested",
      sessionId,
      timestamp: Date.now(),
    });
  }

  transactionSigned(sessionId: string, txHash: string) {
    this.track({
      type: "transaction_signed",
      sessionId,
      txHash,
      timestamp: Date.now(),
    });
  }

  transactionSignatureRejected(sessionId: string) {
    this.track({
      type: "transaction_signature_rejected",
      sessionId,
      timestamp: Date.now(),
    });
  }

  transactionCompleted(
    sessionId: string,
    txHash: string,
    paymentMethod: string,
    amountUSD?: number,
  ) {
    const duration = this.startTime ? Date.now() - this.startTime : 0;

    this.track({
      type: "transaction_completed",
      sessionId,
      txHash,
      paymentMethod,
      success: true,
      amountUSD,
      timestamp: Date.now(),
    });

    this.track({
      type: "conversion_completed",
      sessionId,
      paymentMethod,
      amountUSD,
      duration,
      timestamp: Date.now(),
    });
  }

  transactionFailed(sessionId: string, error: string, paymentMethod: string) {
    this.track({
      type: "transaction_failed",
      sessionId,
      error,
      paymentMethod,
      timestamp: Date.now(),
    });
  }

  onrampWindowOpened(provider: "coinbase" | "moonpay") {
    this.track({
      type: "onramp_window_opened",
      provider,
      timestamp: Date.now(),
    });
  }

  onrampWindowClosed(provider: "coinbase" | "moonpay", completed: boolean) {
    this.track({
      type: "onramp_window_closed",
      provider,
      completed,
      timestamp: Date.now(),
    });
  }

  depositAddressGenerated(
    sessionId: string,
    chainId: string,
    currencySymbol: string,
  ) {
    this.track({
      type: "deposit_address_generated",
      sessionId,
      chainId,
      currencySymbol,
      timestamp: Date.now(),
    });
  }

  depositAddressCopied(sessionId: string) {
    this.track({
      type: "deposit_address_copied",
      sessionId,
      timestamp: Date.now(),
    });
  }

  waitingForDeposit(
    sessionId: string,
    chainId: string,
    currencySymbol: string,
  ) {
    this.track({
      type: "waiting_for_deposit",
      sessionId,
      chainId,
      currencySymbol,
      timestamp: Date.now(),
    });
  }

  depositDetected(sessionId: string, amount: string, currencySymbol: string) {
    this.track({
      type: "deposit_detected",
      sessionId,
      amount,
      currencySymbol,
      timestamp: Date.now(),
    });
  }

  /**
   * Track successful transfer completion (for transfer payment method)
   * This is analogous to transactionCompleted for wallet payments
   */
  transferCompleted(
    sessionId: string,
    txHash: string,
    chainId: string,
    currencySymbol: string,
    amount: string,
    amountUSD?: number,
  ) {
    const duration = this.startTime ? Date.now() - this.startTime : 0;

    this.track({
      type: "transfer_completed",
      sessionId,
      txHash,
      chainId,
      currencySymbol,
      amount,
      amountUSD,
      timestamp: Date.now(),
    });

    this.track({
      type: "conversion_completed",
      sessionId,
      paymentMethod: "transfer",
      amountUSD,
      duration,
      timestamp: Date.now(),
    });
  }

  errorOccurred(errorType: string, errorMessage: string, step?: string) {
    this.track({
      type: "error_occurred",
      errorType,
      errorMessage,
      step: step || this.currentStep,
      timestamp: Date.now(),
    });
  }
}

// Export singleton instance
export const widgetAnalytics = new WidgetAnalytics();
